import { useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { api, newIdempotencyKey } from '@/api/client';
import { formatMoney } from '@/utils';

/**
 * Buy a replacement label for a box that was refunded.
 *
 * The replacement is a new shipment record. The Base44 version overwrote the
 * original row and cleared its refund fields, which erased the evidence that
 * the first label had ever been bought or refunded.
 */
export default function ReShipModal({ shipment, onClose, onSuccess }) {
  const [weight, setWeight] = useState(shipment.parcel_weight_lbs ?? '');
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [step, setStep] = useState('weight');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Generated once per open, so a retry after a timeout cannot buy twice.
  const idempotencyKey = useRef(newIdempotencyKey());

  const handleGetRates = async () => {
    const parsed = Number.parseFloat(weight);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Enter the weight of the box in pounds.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const { rates: fetched } = await api.shipping.reshipRates(shipment.id, parsed);
      if (fetched.length === 0) {
        setError('No carrier offered a rate for this box.');
        return;
      }
      // Already cheapest-first from the server.
      setRates(fetched);
      setSelectedRate(fetched[0]);
      setStep('rates');
    } catch (err) {
      setError(err.message || 'Could not get rates.');
    } finally {
      setLoading(false);
    }
  };

  const handleBuy = async () => {
    if (!selectedRate) {
      setError('Choose a service.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await api.shipping.reship(shipment.id, {
        idempotency_key: idempotencyKey.current,
        rate_id: selectedRate.object_id,
        rate_provider: selectedRate.provider,
        rate_service: selectedRate.service,
        rate_amount: selectedRate.amount,
        weight: Number.parseFloat(weight),
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Could not buy the label.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="card modal-card">
        <div className="modal-header">
          <div>
            <h3>Re-ship box</h3>
            <p>{shipment.shipment_name}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close">
            <X size={22} />
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {step === 'weight' && (
          <>
            <div className="form-group">
              <label className="form-label" htmlFor="reship-weight">
                Weight (lbs)
              </label>
              <input
                id="reship-weight"
                className="form-input"
                type="number"
                min="0.1"
                step="0.1"
                value={weight}
                onChange={(event) => setWeight(event.target.value)}
              />
            </div>
            <button className="btn btn-primary full-width" onClick={handleGetRates} disabled={loading}>
              {loading ? <Loader2 size={18} className="spin" /> : null}
              {loading ? 'Getting rates…' : 'Get rates'}
            </button>
          </>
        )}

        {step === 'rates' && (
          <>
            <ul className="plain-list">
              {rates.map((rate, index) => (
                <li key={rate.object_id}>
                  <label className="rate-row">
                    <input
                      type="radio"
                      name="reship-rate"
                      checked={selectedRate?.object_id === rate.object_id}
                      onChange={() => setSelectedRate(rate)}
                    />
                    <span className="rate-name">
                      {rate.provider} {rate.service}
                      {index === 0 && <span className="badge badge-green">Cheapest</span>}
                    </span>
                    <span className="rate-amount">{formatMoney(rate.amount)}</span>
                    {rate.estimated_days ? (
                      <span className="rate-days">{rate.estimated_days} day(s)</span>
                    ) : null}
                  </label>
                </li>
              ))}
            </ul>

            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setStep('weight')} disabled={loading}>
                Back
              </button>
              <button className="btn btn-primary" onClick={handleBuy} disabled={loading}>
                {loading ? <Loader2 size={18} className="spin" /> : null}
                {loading ? 'Buying…' : `Buy ${formatMoney(selectedRate?.amount)} label`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
